import React, { useState } from 'react';
import { X, Trash2, Edit, Download, Eye, Clock, Package, ExternalLink, Link as LinkIcon, TrendingUp, Users, Activity } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { GET_ITEM_DOWNLOAD_STATS } from '../graphql/queries';
import InventoryManagement from './InventoryManagement';

const ItemDetailPopup = ({ item, isOpen, onClose, onEdit, onDelete, onToggleVisibility }) => {
    if (!isOpen || !item) return null;
    const [showInventoryManagement, setShowInventoryManagement] = useState(false);
    const [activeTab, setActiveTab] = useState('details'); // 'details', 'analytics', 'activity'

    const isDigitalProduct = item.productType === 'DIGITAL' || item.productType === 'HYBRID';
    const isPhysicalProduct = item.productType === 'PHYSICAL' || item.productType === 'HYBRID';

    // Fetch download stats for digital products
    const { data: downloadStats, loading: statsLoading } = useQuery(GET_ITEM_DOWNLOAD_STATS, {
        variables: { itemId: item.id },
        skip: !isDigitalProduct,
        fetchPolicy: 'cache-and-network'
    });

    // Get file and URL assets separately
    const fileAssets = item.digitalAssets?.filter(a => a.assetType === 'FILE') || [];
    const urlAssets = item.digitalAssets?.filter(a => a.assetType === 'URL') || [];

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown size';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatHourstoDays = (hours) => {
        if (hours === -1) return 'Never';

        switch (hours) {
            case 24:
                return '24 hours';
            case 48:
                return '48 hours';
            case 168:
                return '1 week';
            case 720:
                return '1 month';
            default:
                if (hours < 24) return `${hours} hours`;
                const days = Math.floor(hours / 24);
                const remainingHours = hours % 24;
                return `${days} day(s)${remainingHours ? ` and ${remainingHours} hour(s)` : ''}`;
        }
    };


    const formatRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        // Handle invalid dates
        if (isNaN(diffMs)) return 'recently';

        // Handle very recent (less than 1 minute)
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 30) return `${diffDays}d ago`;

        // Handle older dates
        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths < 12) return `${diffMonths}mo ago`;

        const diffYears = Math.floor(diffDays / 365);
        return `${diffYears}y ago`;
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            ACTIVE: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
            EXPIRED: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Expired' },
            EXHAUSTED: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Exhausted' },
            REVOKED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Revoked' }
        };
        const config = statusConfig[status] || statusConfig.ACTIVE;
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white p-4 sm:p-6 border-b border-gray-200 rounded-t-lg z-10">
                    <div className="flex justify-between items-start">
                        <div className="flex flex-wrap gap-2 sm:gap-4">
                            <button
                                onClick={() => onEdit(item)}
                                className="flex items-center px-2 py-1 bg-transparent text-[#191970] rounded-md hover:bg-blue-100 text-sm"
                            >
                                <Edit size={14} className='mr-1 sm:mr-2' />
                                <span className="hidden sm:inline">Edit</span>
                            </button>
                            <button
                                onClick={() => onDelete(item)}
                                className="flex items-center px-2 py-1 bg-transparent text-red-500 rounded-md hover:bg-red-100 text-sm"
                            >
                                <Trash2 size={14} className='mr-1 sm:mr-2' />
                                <span className="hidden sm:inline">Delete</span>
                            </button>
                        </div>

                        {/* Product Type Badge */}
                        <div className="flex items-center gap-2 my-auto">
                            {isDigitalProduct && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    <Download size={12} className="mr-1" />
                                    Digital Product
                                </span>
                            )}
                            {isPhysicalProduct && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <Package size={12} className="mr-1" />
                                    Physical Product
                                </span>
                            )}
                        </div>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 bg-gray-100 p-1 rounded">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Tabs for Digital Products */}
                    {isDigitalProduct && (
                        <div className="flex gap-2 mt-4 bg-gray-100 p-1 rounded-lg">
                            <button
                                onClick={() => setActiveTab('details')}
                                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${activeTab === 'details'
                                    ? 'bg-white text-[#191970] shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                            >
                                <span className="flex items-center justify-center gap-1.5">
                                    <Eye size={16} />
                                    <span className="hidden sm:inline">Details</span>
                                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('analytics')}
                                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${activeTab === 'analytics'
                                    ? 'bg-white text-[#191970] shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                            >
                                <span className="flex items-center justify-center gap-1.5">
                                    <TrendingUp size={16} />
                                    <span className="hidden sm:inline">Analytics</span>
                                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('activity')}
                                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${activeTab === 'activity'
                                    ? 'bg-white text-[#191970] shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                            >
                                <span className="flex items-center justify-center gap-1.5">
                                    <Activity size={16} />
                                    <span className="hidden sm:inline">Activity</span>
                                </span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6">
                    {/* Details Tab */}
                    {activeTab === 'details' && (
                        <div className="space-y-6">
                            {/* Product Image */}
                            {item.image && (
                                <div className="w-full aspect-square relative mx-auto max-w-md rounded-lg overflow-hidden border border-gray-200">
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="absolute top-0 left-0 w-full h-full object-cover"
                                    />
                                </div>
                            )}

                            {/* Product Basic Info with Strike-through Price */}
                            <div className="space-y-2">
                                <h4 className="text-xl sm:text-2xl font-bold text-[#191970] break-words">{item.name}</h4>

                                {/* Price Display with Compare At Price */}
                                <div className="flex items-center gap-3 flex-wrap">
                                    <p className="text-2xl font-bold text-[#191970]">
                                        {item.priceVariants[0]?.price.toLocaleString('en-NG', {
                                            style: 'currency',
                                            currency: 'NGN',
                                            minimumFractionDigits: 0
                                        })}
                                    </p>

                                    {/* Show strike-through price if compareAtPrice exists and is higher */}
                                    {item.priceVariants[0]?.compareAtPrice &&
                                        item.priceVariants[0]?.compareAtPrice > item.priceVariants[0]?.price && (
                                            <>
                                                <p className="text-lg text-gray-500 line-through">
                                                    {item.priceVariants[0]?.compareAtPrice.toLocaleString('en-NG', {
                                                        style: 'currency',
                                                        currency: 'NGN',
                                                        minimumFractionDigits: 0
                                                    })}
                                                </p>
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                                                    Save {Math.round(((item.priceVariants[0]?.compareAtPrice - item.priceVariants[0]?.price) / item.priceVariants[0]?.compareAtPrice) * 100)}%
                                                </span>
                                            </>
                                        )}
                                </div>
                            </div>

                            {/* Description Card */}
                            {item.description && (
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                                            Description
                                        </h3>
                                    </div>
                                    <div className="p-2">
                                        <p className="text-gray-700 text-sm leading-relaxed break-words">{item.description}</p>
                                    </div>
                                </div>
                            )}

                            {/* Digital Content Section - Only for Digital Products */}
                            {isDigitalProduct && (fileAssets.length > 0 || urlAssets.length > 0 || item.digitalSettings) && (
                                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg overflow-hidden">
                                    <div className="bg-purple-100 px-4 py-3 border-b border-purple-200">
                                        <h3 className="text-sm font-semibold text-purple-900 flex items-center">
                                            <Download size={16} className="mr-2" />
                                            Digital Content & Settings
                                        </h3>
                                    </div>
                                    <div className="p-4 space-y-4">
                                        {/* Digital Files */}
                                        {fileAssets.length > 0 && (
                                            <div>
                                                <h4 className="text-xs font-semibold text-purple-900 uppercase tracking-wide mb-3">
                                                    Downloadable Files ({fileAssets.length})
                                                </h4>
                                                <div className="space-y-2">
                                                    {fileAssets.map((asset, index) => (
                                                        <div key={index} className="bg-white border border-purple-200 rounded-lg p-3">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                                        {asset.originalName || asset.filename}
                                                                    </p>
                                                                    <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                                                                        <span className="px-2 py-0.5 bg-gray-100 rounded">{asset.fileType}</span>
                                                                        <span>{formatFileSize(asset.fileSize)}</span>
                                                                    </div>
                                                                </div>
                                                                {getStatusBadge(asset.isActive ? 'ACTIVE' : 'REVOKED')}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Digital Links */}
                                        {urlAssets.length > 0 && (
                                            <div>
                                                <h4 className="text-xs font-semibold text-purple-900 uppercase tracking-wide mb-3">
                                                    External Links ({urlAssets.length})
                                                </h4>
                                                <div className="space-y-2">
                                                    {urlAssets.map((asset, index) => (
                                                        <div key={index} className="bg-white border border-purple-200 rounded-lg p-3">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                                        {asset.originalName || asset.filename}
                                                                    </p>
                                                                    {asset.linkDescription && (
                                                                        <p className="text-xs text-gray-600 mt-1">
                                                                            {asset.linkDescription}
                                                                        </p>
                                                                    )}
                                                                    <div className="flex items-center mt-2 gap-2">
                                                                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                                                            {asset.linkType?.replace('_', ' ')}
                                                                        </span>
                                                                        <a
                                                                            href={asset.linkUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-blue-600 hover:text-blue-800 flex items-center text-xs font-medium"
                                                                        >
                                                                            <ExternalLink size={12} className="mr-1" />
                                                                            Open Link
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                                {getStatusBadge(asset.isActive ? 'ACTIVE' : 'REVOKED')}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Access Settings */}
                                        {item.digitalSettings && (
                                            <div className="pt-4 border-t border-purple-200">
                                                <h4 className="text-xs font-semibold text-purple-900 uppercase tracking-wide mb-3 flex items-center">
                                                    <Clock size={14} className="mr-1.5" />
                                                    Access Rules
                                                </h4>
                                                <div className="bg-white rounded-lg p-4 border border-purple-200">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <span className="text-gray-500 text-xs uppercase tracking-wide">Download Limit</span>
                                                            <p className="font-semibold text-gray-900 mt-1">
                                                                {item.digitalSettings.downloadLimit === -1
                                                                    ? 'Unlimited'
                                                                    : `${item.digitalSettings.downloadLimit || 5} times`}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500 text-xs uppercase tracking-wide">Access Duration</span>
                                                            <p className="font-semibold text-gray-900 mt-1">
                                                                {item.digitalSettings.linkExpiryHours === -1
                                                                    ? 'No expiration'
                                                                    : formatHourstoDays(item.digitalSettings.linkExpiryHours || 48)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Inventory Management - Only for Physical Products */}
                            {isPhysicalProduct && (
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                                            <Package size={16} className="mr-2 text-gray-600" />
                                            Inventory
                                        </h3>
                                    </div>
                                    <div className="p-4">
                                        <button
                                            type="button"
                                            onClick={() => setShowInventoryManagement(true)}
                                            className="w-full px-4 py-2.5 text-sm font-medium text-[#191970] bg-blue-50 border border-[#191970] rounded-lg hover:bg-blue-100 transition-colors"
                                        >
                                            Manage Inventory
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Schedule Information */}
                            {(item.scheduledStart || item.scheduledEnd) && (
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                                            <Clock size={16} className="mr-2 text-gray-600" />
                                            Schedule
                                        </h3>
                                    </div>
                                    <div className="p-4 space-y-2 text-sm">
                                        {item.scheduledStart && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Starts:</span>
                                                <span className="font-medium text-gray-900">
                                                    {new Date(item.scheduledStart).toLocaleString()}
                                                </span>
                                            </div>
                                        )}
                                        {item.scheduledEnd && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Ends:</span>
                                                <span className="font-medium text-gray-900">
                                                    {new Date(item.scheduledEnd).toLocaleString()}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Repeat Availability */}
                            {item.repeatAvailability && item.repeatAvailability.length > 0 && (
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                                            <Clock size={16} className="mr-2 text-gray-600" />
                                            Recurring Availability
                                        </h3>
                                    </div>
                                    <div className="p-4 space-y-2">
                                        {item.repeatAvailability.map((repeat, index) => (
                                            <div key={index} className="text-sm text-gray-700 flex items-center">
                                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                                {repeat.dayOfWeek !== undefined && `Day ${repeat.dayOfWeek}`}
                                                {repeat.startTime && ` from ${repeat.startTime}`}
                                                {repeat.endTime && ` to ${repeat.endTime}`}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}


                    {/* Analytics Tab */}
                    {activeTab === 'analytics' && isDigitalProduct && (
                        <div className="space-y-6">
                            {statsLoading ? (
                                <div className="text-center py-8 text-gray-500">Loading analytics...</div>
                            ) : downloadStats?.getItemDownloadStats ? (
                                <>
                                    {/* Main Stats - Show both total purchases and unique customers */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
                                            <p className="text-3xl font-bold text-[#191970] mb-1">
                                                {downloadStats.getItemDownloadStats.totalPurchases}
                                            </p>
                                            <p className="text-sm text-gray-600">Total Purchases</p>
                                            {downloadStats.getItemDownloadStats.uniqueCustomers > 0 &&
                                                downloadStats.getItemDownloadStats.uniqueCustomers !== downloadStats.getItemDownloadStats.totalPurchases && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {downloadStats.getItemDownloadStats.uniqueCustomers} unique customer{downloadStats.getItemDownloadStats.uniqueCustomers !== 1 ? 's' : ''}
                                                    </p>
                                                )}
                                        </div>

                                        <div className="bg-white border-2 border-green-200 rounded-lg p-4">
                                            <p className="text-3xl font-bold text-[#191970] mb-1">
                                                {downloadStats.getItemDownloadStats.totalDownloads}
                                            </p>
                                            <p className="text-sm text-gray-600">Times Accessed</p>
                                        </div>
                                    </div>

                                    {/* Link Status - Clean List */}
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <h4 className="font-semibold text-gray-900 mb-3 text-sm">Access Links Status</h4>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-gray-600 flex items-center">
                                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                                    Active
                                                </span>
                                                <span className="font-medium text-gray-900">
                                                    {downloadStats.getItemDownloadStats.activeLinks}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-gray-600 flex items-center">
                                                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                                                    Expired
                                                </span>
                                                <span className="font-medium text-gray-900">
                                                    {downloadStats.getItemDownloadStats.expiredLinks}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-gray-600 flex items-center">
                                                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                                                    Exhausted
                                                </span>
                                                <span className="font-medium text-gray-900">
                                                    {downloadStats.getItemDownloadStats.exhaustedLinks}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Current Settings Info */}
                                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                        <p className="text-sm text-gray-700">
                                            <span className="font-semibold text-[#191970]">Current settings:</span> Customers can access{' '}
                                            <span className="font-semibold">
                                                {item.digitalSettings?.downloadLimit === -1
                                                    ? 'unlimited times'
                                                    : `up to ${item.digitalSettings?.downloadLimit || 5} times`}
                                            </span>
                                            {item.digitalSettings?.linkExpiryHours !== -1 && (
                                                <>
                                                    {' '}within{' '}
                                                    <span className="font-semibold">
                                                        {formatHourstoDays(item.digitalSettings?.linkExpiryHours || 48)}
                                                    </span>
                                                    {' '}of purchase
                                                </>
                                            )}
                                            {item.digitalSettings?.linkExpiryHours === -1 && (
                                                <span> with no expiration</span>
                                            )}
                                            .
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Download size={48} className="mx-auto mb-3 opacity-30" />
                                    <p className="font-medium">No sales yet</p>
                                    <p className="text-sm mt-2">Analytics will appear after your first sale</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Activity Tab */}
                    {activeTab === 'activity' && isDigitalProduct && (
                        <div className="space-y-4">
                            {statsLoading ? (
                                <div className="text-center py-8 text-gray-500">Loading activity...</div>
                            ) : downloadStats?.getItemDownloadStats?.recentActivity?.length > 0 ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-gray-900 text-sm">Customer Purchase History</h4>
                                    </div>
                                    <div className="space-y-2">
                                        {downloadStats.getItemDownloadStats.recentActivity.map((activity, index) => (
                                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <p className="font-medium text-gray-900 text-sm truncate">
                                                                {activity.customerName || 'Customer'}
                                                            </p>
                                                            {getStatusBadge(activity.status)}
                                                        </div>
                                                        <p className="text-xs text-gray-500 truncate mb-2">
                                                            {activity.customerEmail}
                                                        </p>
                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs text-gray-600">
                                                            <span className="flex items-center">
                                                                <Download size={12} className="mr-1" />
                                                                Downloaded {activity.downloadCount} time{activity.downloadCount !== 1 ? 's' : ''}
                                                            </span>
                                                            <span className="hidden sm:inline">•</span>
                                                            <span className="flex items-center">
                                                                <Clock size={12} className="mr-1" />
                                                                Purchased {formatRelativeTime(activity.createdAt)}
                                                            </span>
                                                        </div>
                                                        {activity.expiresAt && activity.status === 'ACTIVE' && new Date(activity.expiresAt) > new Date() && (
                                                            <div className="mt-2 pt-2 border-t border-gray-100">
                                                                <p className="text-xs text-orange-600">
                                                                    Access expires {formatRelativeTime(activity.expiresAt)}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {activity.status === 'EXPIRED' && activity.expiresAt && (
                                                            <div className="mt-2 pt-2 border-t border-gray-100">
                                                                <p className="text-xs text-gray-500">
                                                                    Access expired {formatRelativeTime(activity.expiresAt)}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {activity.status === 'EXHAUSTED' && (
                                                            <div className="mt-2 pt-2 border-t border-gray-100">
                                                                <p className="text-xs text-orange-600">
                                                                    Download limit reached
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {downloadStats.getItemDownloadStats.recentActivity.length >= 5 && (
                                        <p className="text-xs text-gray-500 text-center pt-2">
                                            Showing most recent 5 purchases
                                        </p>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Activity size={48} className="mx-auto mb-3 opacity-30" />
                                    <p className="font-medium">No purchases yet</p>
                                    <p className="text-sm mt-2">Customer purchase history will appear here</p>
                                </div>
                            )}
                        </div>
                    )}




                </div>

                {/* Inventory Management Modal */}
                {showInventoryManagement && (
                    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
                            <InventoryManagement
                                item={item}
                                onClose={() => setShowInventoryManagement(false)}
                                catalogId={item.catalogId}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ItemDetailPopup;